import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Filter, X } from 'lucide-react'
import Select from 'react-select'

const MuleSoftFilter = ({
  firstName,
  setFirstName,
  lastName,
  setLastName,
  startDateTime,
  setStartDateTime,
  endDateTime,
  setEndDateTime,
  birthDate,
  setBirthDate,
  setFiltersInitialized,
  onSubmit,
  onReset,
  setTimezone,
  timezone,
  isLoading
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [errors, setErrors] = useState({})

  useEffect(() => {
    const f = searchParams.get('firstName')
    const l = searchParams.get('lastName')
    const sdt = searchParams.get('startDateTime')
    const edt = searchParams.get('endDateTime')
    const bd = searchParams.get('birthDate')

    if (f) setFirstName(f)
    if (l) setLastName(l)
    if (sdt) setStartDateTime(sdt)
    if (edt) setEndDateTime(edt)
    if (bd) setBirthDate(bd)

    setFiltersInitialized(true)
  }, [])

  useEffect(() => {
    const params = {}
    if (firstName) params.firstName = firstName
    if (lastName) params.lastName = lastName
    if (startDateTime) params.startDateTime = startDateTime
    if (endDateTime) params.endDateTime = endDateTime
    if (birthDate) params.birthDate = birthDate

    setSearchParams(params)
  }, [firstName, lastName, startDateTime, endDateTime, birthDate])

  const handleApply = () => {
    const newErrors = {}

    if (!firstName) newErrors.firstName = 'This field is required'
    if (!lastName) newErrors.lastName = 'This field is required'
    if (!birthDate) newErrors.birthDate = 'This field is required'
    if (!timezone) newErrors.timezone = 'This field is required'
    if (!startDateTime) newErrors.startDateTime = 'This field is required'
    setErrors(newErrors)

    if (Object.keys(newErrors).length > 0) return
    onSubmit()
  }

  const handleClear = () => {
    setErrors({})
    onReset()
  }

  return (
    <div className='container mx-auto bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-8'>
      <p className='text-sm text-gray-600 mb-4'>
        <span className='text-red-600 font-bold'>*</span>{' '}
        <b>Fields marked with an asterisk are mandatory.</b>
      </p>
      <div className='grid grid-cols-1 md:grid-cols-4 gap-6'>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            <b>Type of Caregiver Match</b>
          </label>
          <Select
            isDisabled={true}
            defaultValue={{ label: 'Callout', value: 'call-out' }}
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            <b>
              Client First Name <span className='text-red-600'>*</span>
            </b>
          </label>
          <input
            type='text'
            value={firstName}
            onChange={e => {
              setFirstName(e.target.value)
            }}
            className='w-full px-3 py-2 border rounded-lg'
            placeholder='Enter first name'
          />
          {errors.firstName && (
            <p className='text-red-600 text-xs mt-1'>{errors.firstName}</p>
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            <b>
              Client Last Name <span className='text-red-600'>*</span>
            </b>
          </label>
          <input
            type='text'
            value={lastName}
            onChange={e => {
              setLastName(e.target.value)
            }}
            className='w-full px-3 py-2 border rounded-lg'
            placeholder='Enter last name'
          />
          {errors.lastName && (
            <p className='text-red-600 text-xs mt-1'>{errors.lastName}</p>
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            <b>
              Birth Date <span className='text-red-600'>*</span>
            </b>
          </label>
          <input
            type='date'
            value={birthDate}
            onChange={e => {
              setBirthDate(e.target.value)
            }}
            className='w-full px-3 py-2 border rounded-lg'
          />
          {errors.birthDate && (
            <p className='text-red-600 text-xs mt-1'>{errors.birthDate}</p>
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            <b>
              Select Client Time Zone <span className='text-red-600'>*</span>
            </b>
          </label>
          <Select
            className='w-full'
            value={{
              label:
                timezone === 'America/New_York'
                  ? 'PA (Eastern Time)'
                  : 'IL (Central Time)',
              value: timezone
            }}
            onChange={option => {
              setTimezone(option.value)
            }}
            options={[
              { value: 'America/New_York', label: 'PA (Eastern Time)' },
              { value: 'America/Chicago', label: 'IL (Central Time)' }
            ]}
          />
          {errors.timezone && (
            <p className='text-red-600 text-xs mt-1'>{errors.timezone}</p>
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            <b>Schedule Start Date & Time *</b>
          </label>
          <input
            type='datetime-local'
            value={startDateTime}
            onChange={e => {
              setStartDateTime(e.target.value)
            }}
            className='w-full px-3 py-2 border rounded-lg'
          />
          {errors.startDateTime && (
            <p className='text-red-600 text-xs mt-1'>{errors.startDateTime}</p>
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-2'>
            <b>Schedule End Date & Time</b>
          </label>
          <input
            type='datetime-local'
            value={endDateTime}
            disabled={true}
            onChange={e => {
              setEndDateTime(e.target.value)
            }}
            className='w-full px-3 py-2 border rounded-lg'
          />
        </div>
      </div>

      <div className='flex justify-center gap-4 mt-10'>
        <button
          onClick={handleApply}
          disabled={isLoading} 
          className={`px-5 py-2 rounded-lg flex items-center gap-2 ${
            isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600'
          } text-white`}
        >
          <Filter className='w-4 h-4' />
          {isLoading ? 'Applying...' : 'Apply Filters'}
        </button>

        <button
          onClick={handleClear}
          className='px-5 py-2 border border-gray-300 rounded-lg flex items-center gap-2'
        >
          <X className='w-4 h-4' /> Clear
        </button>
      </div>
    </div>
  )
}

export default MuleSoftFilter
